import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "458",
  "slug": "what-is-a-tsunami",
  "title": "What Is a Tsunami? How the Ocean’s Most Powerful Waves Form",
  "excerpt": "A tsunami is a series of immense waves set off by the sudden displacement of ocean water. Here is what triggers them, how they behave, and why the term \"tidal wave\" is wrong.",
  "date": "2026-06-16",
  "category": "Education",
  "image": "https://images.unsplash.com/photo-1518837695005-2083093ee35b?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxvY2VhbiUyMHdhdmUlMjBwb3dlcnxlbnwxfDB8fHwxNzgxNTk3NjY2fDA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "5 min",
  "author": "EarthGuessr Team",
  "tldr": "A tsunami is a series of ocean waves caused by the sudden displacement of a large volume of water, usually by an undersea earthquake. In deep ocean they travel as fast as a jet plane, then pile up into towering walls of water as they reach the coast.",
  "content": [
    {
      "type": "paragraph",
      "text": "A tsunami is a series of powerful ocean waves generated by the sudden displacement of a large volume of water. The name comes from Japanese, meaning \"harbour wave,\" a reminder of how often these events have struck the coasts of Japan. They rank among the most destructive natural phenomena on the planet, and they behave nothing like the ordinary waves we see at the beach."
    },
    {
      "type": "paragraph",
      "text": "Understanding how a tsunami forms and moves is not just fascinating geography; it is knowledge that saves lives."
    },
    {
      "type": "heading",
      "text": "What Triggers a Tsunami"
    },
    {
      "type": "paragraph",
      "text": "Almost any event that abruptly shoves a huge mass of water can spawn a tsunami, but a few causes dominate."
    },
    {
      "type": "list",
      "items": [
        "Undersea earthquakes — by far the most common cause. When a megathrust earthquake at a subduction zone suddenly lifts or drops the sea floor, the entire column of water above it is displaced.",
        "Landslides — massive slumps of rock or sediment, either underwater or sliding into the sea from above, can launch enormous local waves.",
        "Volcanic eruptions — explosive eruptions or the collapse of an island volcano can displace vast amounts of water.",
        "Impacts — extremely rare, but a large meteorite striking the ocean would generate a tsunami."
      ]
    },
    {
      "type": "heading",
      "text": "How a Tsunami Behaves"
    },
    {
      "type": "paragraph",
      "text": "What makes a tsunami so dangerous is its physics. In the open ocean, a tsunami has an enormous wavelength, the distance between crests can be a hundred kilometres or more, yet a height of only a metre or so. Ships in deep water often do not even notice one passing beneath them. But it races across the sea at the speed of a jet airliner, often 500 to 800 kilometres per hour."
    },
    {
      "type": "paragraph",
      "text": "Everything changes when it reaches shallow water. As the sea floor rises toward the coast, the front of the wave slows down while the water behind it piles up, a process called shoaling. The wavelength compresses and the energy is forced upward, transforming a barely-noticeable swell into a towering wall of water that can surge far inland."
    },
    {
      "type": "heading",
      "text": "Not a Tidal Wave"
    },
    {
      "type": "paragraph",
      "text": "Tsunamis are often called \"tidal waves,\" but the term is a misnomer that scientists have worked hard to retire. Tides are the gentle, predictable rise and fall of the sea caused by the gravity of the Moon and Sun. A tsunami has nothing to do with tides; it is driven by a sudden geologic shock. Calling it a tidal wave confuses two completely different forces."
    },
    {
      "type": "heading",
      "text": "The Ring of Fire Connection"
    },
    {
      "type": "paragraph",
      "text": "Most of the world’s tsunamis are born around the Pacific Ring of Fire, the horseshoe-shaped belt of subduction zones and volcanoes that rims the Pacific Ocean. This is where the planet’s great megathrust earthquakes occur, and it is no coincidence that the coasts of Japan, Chile, Alaska, and Indonesia have all been struck by major tsunamis. Where plates collide and the sea floor jolts, the ocean answers with waves."
    },
    {
      "type": "heading",
      "text": "Warning Signs and Systems"
    },
    {
      "type": "paragraph",
      "text": "Nature often gives a warning. One of the most reliable signs is drawback: just before a tsunami arrives, the sea may suddenly retreat far beyond the normal low-tide line, exposing the sea floor. That receding water is the trough of the wave, and it means a crest is coming. The standard advice is simple: feel a strong coastal earthquake or see the sea pull back, and head for high ground immediately."
    },
    {
      "type": "paragraph",
      "text": "On top of natural signs, a global network of seismic sensors and deep-ocean pressure buoys now detects tsunamis and issues alerts, giving distant coastlines precious time to evacuate."
    },
    {
      "type": "heading",
      "text": "How Far Can a Tsunami Travel?"
    },
    {
      "type": "paragraph",
      "text": "Unlike a normal wind-driven wave, which spends its energy and fades within sight of where it formed, a tsunami can cross an entire ocean basin. Because it carries so much energy and loses very little of it in deep water, a tsunami generated on one side of an ocean can still arrive as a dangerous wave on the far shore many hours later. Scientists call these long-range events teletsunamis."
    },
    {
      "type": "paragraph",
      "text": "A great earthquake off the coast of one continent can therefore threaten beaches thousands of kilometres away, on the opposite side of the ocean, while people there are going about an ordinary day. This is precisely why ocean-wide warning networks exist: the hours it takes a wave to cross open water are exactly the hours that coastal communities need to move to safety."
    },
    {
      "type": "heading",
      "text": "A Planet of Moving Plates"
    },
    {
      "type": "paragraph",
      "text": "Tsunamis are a vivid reminder that the Earth’s surface is alive and shifting. The same plate movements that build mountains and open oceans can, in an instant, send walls of water across entire ocean basins. Curious about the restless geography behind these forces? Explore the world from above in EarthGuessr and get to know the coastlines and fault lines that shape our planet."
    }
  ]
};
